/**
 * UserFormModal — create / edit a user (Phase 10.4 + 10.5 back-fill).
 *
 * Two modes share the modal:
 *  - create: nick + heslo required; admin/lms_id/barva default to 0/'';
 *  - edit: heslo is optional (omit to keep current); a Členství block
 *    lists the user's company memberships with an inline primárni toggle
 *    + Odebrat + Přidat firmu picker. Per-member module grants live in
 *    the Členové tab (access overhaul Stage 6).
 *
 * Password is MD5'd client-side before POST/PUT to match the API schema
 * (a 32-char lowercase hex MD5 of the plaintext).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { Field, Fields } from '@/components/Field';
import { Icon } from '@/components/Icon';
import { Modal, DialogCrown } from '@/components/Modal';
import { Skeleton } from '@/components/Skeleton';
import { TextInput } from '@/components/TextInput';
import { toast } from '@/components/Toaster';
import { useConfirm } from '@/components/ConfirmDialog';
import { ApiError } from '@/lib/api';
import { md5Hex } from '@/lib/md5';
import {
  useActivateUserMutation,
  useAddUserMembershipMutation,
  useCreateUserMutation,
  useDeleteUserMutation,
  useRemoveUserMembershipMutation,
  useUpdateMembershipLmsPobockyMutation,
  useUpdateUserMembershipMutation,
  useUpdateUserMutation,
  useUserMembershipsQuery,
  type MgmtMembership,
  type MgmtUser,
  type UserCreateBody,
  type UserUpdateBody,
} from './api';
import { MembershipPickerModal } from './MembershipPickerModal';
import { UserDeactivateModal } from './UserDeactivateModal';

interface UserFormModalProps {
  open: boolean;
  mode: 'create' | 'edit';
  user?: MgmtUser;
  onOpenChange: (open: boolean) => void;
}

interface UserFormState {
  nick: string;
  heslo: string;
  jmeno: string;
  prijmeni: string;
  osloveni: string;
  email: string;
  admin: boolean;
  lms_id: string;
  barva: string;
}

export function UserFormModal({ open, mode, user, onOpenChange }: UserFormModalProps) {
  const create = useCreateUserMutation();
  const update = useUpdateUserMutation();
  const remove = useDeleteUserMutation();
  const activate = useActivateUserMutation();
  const { confirm, dialog: confirmDialog } = useConfirm();

  const [form, setForm] = useState<UserFormState>(emptyForm());
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const firstFieldRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && user) {
      setForm({
        nick: user.nick,
        heslo: '',
        jmeno: user.jmeno,
        prijmeni: user.prijmeni,
        osloveni: user.osloveni,
        email: user.email,
        admin: user.admin === 1,
        lms_id: String(user.lms_id ?? 0),
        barva: user.barva ?? '',
      });
    } else {
      setForm(emptyForm());
    }
    setConfirmingDelete(false);
    queueMicrotask(() => firstFieldRef.current?.focus());
  }, [open, mode, user]);

  const submit = () => {
    const nick = form.nick.trim();
    if (!nick) {
      toast.error('Zadejte nick.');
      return;
    }
    if (mode === 'create' && !form.heslo) {
      toast.error('Zadejte heslo.');
      return;
    }
    const lmsId = form.lms_id ? Number.parseInt(form.lms_id, 10) : 0;
    if (!Number.isFinite(lmsId) || lmsId < 0) {
      toast.error('Neplatné LMS ID.');
      return;
    }
    const admin: 0 | 1 = form.admin ? 1 : 0;

    if (mode === 'edit' && user) {
      const body: UserUpdateBody = {
        nick,
        jmeno: form.jmeno.trim(),
        prijmeni: form.prijmeni.trim(),
        osloveni: form.osloveni.trim(),
        email: form.email.trim(),
        admin,
        lms_id: lmsId,
        barva: form.barva.trim(),
      };
      if (form.heslo) body.heslo = md5Hex(form.heslo);
      update.mutate(
        { id: user.id_uzivatele, body },
        {
          onSuccess: () => {
            toast.ok('Uživatel aktualizován');
            onOpenChange(false);
          },
          onError: (err) => toast.error(apiMessage(err, 'Nepodařilo se uložit.')),
        },
      );
      return;
    }

    const body: UserCreateBody = {
      nick,
      heslo: md5Hex(form.heslo),
      jmeno: form.jmeno.trim(),
      prijmeni: form.prijmeni.trim(),
      osloveni: form.osloveni.trim(),
      email: form.email.trim(),
      admin,
      lms_id: lmsId,
      barva: form.barva.trim(),
    };
    create.mutate(body, {
      onSuccess: () => {
        toast.ok('Uživatel vytvořen');
        onOpenChange(false);
      },
      onError: (err) => toast.error(apiMessage(err, 'Nepodařilo se vytvořit.')),
    });
  };

  const onActivate = async () => {
    if (!user) return;
    const ok = await confirm({
      eyebrow: 'Aktivace uživatele',
      tone: 'warn',
      title: (
        <>
          Znovu aktivovat <em>uživatele</em>?
        </>
      ),
      description:
        'Uživatel se bude moci znovu přihlásit a objeví se ve výběrech. Data převedená při deaktivaci zůstávají nástupcům.',
      confirmLabel: 'Aktivovat',
    });
    if (!ok) return;
    activate.mutate(user.id_uzivatele, {
      onSuccess: () => {
        toast.ok('Uživatel aktivován');
        onOpenChange(false);
      },
      onError: (err) => toast.error(apiMessage(err, 'Aktivace se nepodařila.')),
    });
  };

  const onDelete = () => {
    if (!user) return;
    if (!confirmingDelete) {
      setConfirmingDelete(true);
      return;
    }
    remove.mutate(user.id_uzivatele, {
      onSuccess: () => {
        toast.ok('Uživatel smazán');
        onOpenChange(false);
      },
      onError: (err) => toast.error(apiMessage(err, 'Nepodařilo se smazat.')),
    });
  };

  const isPending = create.isPending || update.isPending;
  const heading = mode === 'edit' ? 'Úprava uživatele' : 'Nový uživatel';

  return (
    <Modal open={open} onOpenChange={onOpenChange} maxWidth={720}>
      <DialogCrown
        tone="neutral"
        eyebrow={heading}
        eyebrowMeta={mode === 'edit' && user ? `#${user.id_uzivatele}` : undefined}
        title={
          mode === 'edit' ? (
            <>
              Upravit <em>uživatele</em>.
            </>
          ) : (
            <>
              Vytvořit <em>nového uživatele</em>.
            </>
          )
        }
      />
      <Modal.Body className="px-8 py-6">
        <Fields cols={2}>
          <Field label="Nick" required>
            <TextInput
              ref={firstFieldRef}
              value={form.nick}
              maxLength={20}
              onChange={(e) => setForm({ ...form, nick: e.target.value })}
            />
          </Field>
          <Field
            label={mode === 'edit' ? 'Heslo (volitelné)' : 'Heslo'}
            required={mode === 'create'}
            hint={mode === 'edit' ? 'Ponechte prázdné pro zachování stávajícího hesla.' : undefined}
          >
            <TextInput
              type="password"
              value={form.heslo}
              autoComplete="new-password"
              onChange={(e) => setForm({ ...form, heslo: e.target.value })}
            />
          </Field>

          <Field label="Jméno">
            <TextInput
              value={form.jmeno}
              maxLength={20}
              onChange={(e) => setForm({ ...form, jmeno: e.target.value })}
            />
          </Field>
          <Field label="Příjmení">
            <TextInput
              value={form.prijmeni}
              maxLength={30}
              onChange={(e) => setForm({ ...form, prijmeni: e.target.value })}
            />
          </Field>

          <Field label="Oslovení">
            <TextInput
              value={form.osloveni}
              maxLength={25}
              onChange={(e) => setForm({ ...form, osloveni: e.target.value })}
            />
          </Field>
          <Field label="E-mail">
            <TextInput
              type="email"
              value={form.email}
              maxLength={40}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </Field>

          <Field
            label="LMS ID prodejce"
            hint="0 = není prodejcem v LMS. Pobočky LMS se přiřazují níže u členství ve firmě."
          >
            <TextInput
              type="number"
              value={form.lms_id}
              onChange={(e) => setForm({ ...form, lms_id: e.target.value })}
            />
          </Field>
          <Field label="Barva v kalendáři" hint="CSS barva (např. #f4d300 nebo „yellow“).">
            <TextInput
              value={form.barva}
              onChange={(e) => setForm({ ...form, barva: e.target.value })}
            />
          </Field>

          <Field label="Role" span>
            <Checkbox
              label="Administrátor platformy (Platforma: firmy, uživatelé, LMS; správa všech firem)"
              checked={form.admin}
              onChange={(e) => setForm({ ...form, admin: e.target.checked })}
            />
          </Field>
        </Fields>

        {mode === 'edit' && user && (
          <MembershipsBlock userId={user.id_uzivatele} savedLmsId={user.lms_id ?? 0} />
        )}
      </Modal.Body>
      <Modal.Foot
        meta={
          mode === 'edit' ? (
            <span>
              <b>Uložením</b> přepíšete uživatele. Heslo se mění jen, pokud je vyplněné.
            </span>
          ) : (
            'Esc · zavřít · Enter · uložit'
          )
        }
        actions={
          <>
            {mode === 'edit' && user && (
              <Button
                variant="secondary"
                size="md"
                onClick={user.aktivni === 0 ? onActivate : () => setDeactivateOpen(true)}
                disabled={activate.isPending}
              >
                {user.aktivni === 0 ? 'Aktivovat' : 'Deaktivovat'}
              </Button>
            )}
            {mode === 'edit' && (
              <Button
                variant="secondary"
                size="md"
                onClick={onDelete}
                disabled={remove.isPending}
              >
                {confirmingDelete ? 'Opravdu smazat?' : 'Smazat'}
              </Button>
            )}
            <Button variant="secondary" size="md" onClick={() => onOpenChange(false)}>
              Zrušit
            </Button>
            <Button variant="primary" size="md" onClick={submit} disabled={isPending}>
              {isPending ? 'Ukládám…' : mode === 'edit' ? 'Uložit' : 'Vytvořit'}
            </Button>
          </>
        }
      />
      {confirmDialog}
      <UserDeactivateModal
        open={deactivateOpen}
        user={user}
        onOpenChange={setDeactivateOpen}
        onDeactivated={() => onOpenChange(false)}
      />
    </Modal>
  );
}

function emptyForm(): UserFormState {
  return {
    nick: '',
    heslo: '',
    jmeno: '',
    prijmeni: '',
    osloveni: '',
    email: '',
    admin: false,
    lms_id: '0',
    barva: '',
  };
}

function apiMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.body && typeof err.body === 'object') {
    const e = (err.body as { error?: string }).error;
    if (typeof e === 'string') return e;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

interface MembershipsBlockProps {
  userId: number;
  /** The saved LMS ID — locations need one (the server checks the stored value). */
  savedLmsId: number;
}

function MembershipsBlock({ userId, savedLmsId }: MembershipsBlockProps) {
  const memberships = useUserMembershipsQuery(userId);
  const add = useAddUserMembershipMutation();
  const updateMembership = useUpdateUserMembershipMutation();
  const removeMembership = useRemoveUserMembershipMutation();
  const updateLmsPobocky = useUpdateMembershipLmsPobockyMutation();
  const [pickerOpen, setPickerOpen] = useState(false);

  const rows = memberships.data?.memberships ?? [];
  const sorted = useMemo(
    () => [...rows].sort((a, b) => (b.primarni - a.primarni) || (a.id_firmy - b.id_firmy)),
    [rows],
  );
  const excludeIds = sorted.map((m) => m.id_firmy);

  const setLmsPobocka = (m: MgmtMembership, pobockaId: number, assigned: boolean) => {
    const current = m.lms_pobocky ?? [];
    const pobocky = assigned ? [...current, pobockaId] : current.filter((id) => id !== pobockaId);
    updateLmsPobocky.mutate(
      { userId, companyId: m.id_firmy, pobocky },
      { onError: (err) => toast.error(apiMessage(err, 'Pobočky LMS se nepodařilo uložit.')) },
    );
  };

  const setPrimarni = (m: MgmtMembership, value: 0 | 1) => {
    updateMembership.mutate(
      { userId, companyId: m.id_firmy, body: { primarni: value } },
      {
        onError: (err) => toast.error(apiMessage(err, 'Nepodařilo se uložit.')),
      },
    );
  };

  return (
    <section className="mt-6 border-t border-line pt-5">
      <header className="mb-3 flex items-center gap-2 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-ink-3 max-sm:flex-wrap">
        <span aria-hidden className="inline-block h-px w-3.5 bg-accent" />
        <span>Členství ve firmách</span>
        <span className="ml-auto text-[10px] text-ink-3">
          {memberships.isLoading ? 'Načítání…' : `${sorted.length} záznamů`}
        </span>
        <Button
          size="sm"
          icon={<Icon name="plus" size={12} />}
          onClick={() => setPickerOpen(true)}
        >
          Přidat firmu
        </Button>
      </header>
      <p className="mb-3 font-mono text-[10.5px] text-ink-3">
        Práva modulů spravuje správce firmy v Členech. Pobočky LMS určují, čí leady prodejce vidí a
        přebírá — bez pobočky nemá k leadům přístup.
      </p>

      {memberships.isLoading ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
      ) : sorted.length === 0 ? (
        <p className="px-1 py-3 text-center font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
          Žádná členství. Přidejte první tlačítkem výše.
        </p>
      ) : (
        <ul className="divide-y divide-line border border-line bg-paper">
          {sorted.map((m) => (
            <li key={m.id} className="px-4 py-2.5 text-[12.5px]" data-testid="user-membership">
              <div className="flex items-center gap-3 max-sm:flex-wrap">
                <span className="font-mono text-[11px] text-ink-3 tabular-nums">#{m.id_firmy}</span>
                <span className="truncate text-ink">{m.Nazev || 'Firma'}</span>
                <span className="ml-auto flex items-center gap-4">
                  <Checkbox
                    label="Primární"
                    checked={m.primarni === 1}
                    onChange={(e) => setPrimarni(m, e.target.checked ? 1 : 0)}
                  />
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      removeMembership.mutate(
                        { userId, companyId: m.id_firmy },
                        {
                          onSuccess: () => toast.ok('Členství odebráno'),
                          onError: (err) =>
                            toast.error(apiMessage(err, 'Nepodařilo se odebrat.')),
                        },
                      );
                    }}
                  >
                    Odebrat
                  </Button>
                </span>
              </div>
              {(m.firma_lms_pobocky?.length ?? 0) > 0 && (
                <div
                  className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5"
                  data-testid="user-membership-lms"
                >
                  <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-ink-3">
                    Pobočky LMS
                  </span>
                  {savedLmsId > 0 ? (
                    m.firma_lms_pobocky!.map((p) => (
                      <Checkbox
                        key={p.id}
                        label={p.nazev || `LMS #${p.id}`}
                        aria-label={`${m.Nazev || 'Firma'} — LMS pobočka ${p.nazev || p.id}`}
                        checked={(m.lms_pobocky ?? []).includes(p.id)}
                        disabled={updateLmsPobocky.isPending}
                        onChange={(e) => setLmsPobocka(m, p.id, e.target.checked)}
                      />
                    ))
                  ) : (
                    <span className="font-mono text-[10.5px] text-ink-3">
                      Nejdřív nastavte a uložte LMS ID prodejce.
                    </span>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <MembershipPickerModal
        open={pickerOpen}
        kind="company"
        excludeIds={excludeIds}
        onOpenChange={setPickerOpen}
        onSelect={(c) => {
          add.mutate(
            { userId, body: { id_firmy: c.id_firmy } },
            {
              onSuccess: () => toast.ok(`${c.Nazev} přidána`),
              onError: (err) => toast.error(apiMessage(err, 'Nepodařilo se přidat.')),
            },
          );
        }}
      />
    </section>
  );
}
